import { buildingsByIds, YEARS } from './engine';
import { routeOf } from './construction';
import type { Plan } from './types';

export function downloadText(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const escapeCell = (value: string) => `"${value.replace(/"/g, '""')}"`;
export function exportPhases(plan: Plan, pipeCount: number) {
  const buildings = new Map(buildingsByIds(plan.buildingIds ?? []).map((building) => [building.id, building]));
  const route = routeOf(plan, pipeCount);
  const rows = [
    ['type', 'id', 'annee', 'mode', 'adresse', 'usage', 'conso_MWh_an'],
    ...(plan.buildingIds ?? []).map((id) => {
      const b = buildings.get(id);
      return ['batiment', id, plan.buildingPhases?.[id] ?? YEARS[0], 'manuel', b?.address ?? '', b?.usage ?? '', b?.heatKnown ? String(b.heat) : ''];
    }),
    ...route.indexes.map((index) => ['canalisation', String(index + 1), route.phases[index],
      plan.pipePhaseOverrides?.[index] ? 'manuel' : 'auto', '', '', '']),
  ];
  return '\uFEFF' + rows.map((row) => row.map(escapeCell).join(';')).join('\r\n');
}

function csvRows(text: string): string[][] {
  const clean = text.replace(/^\uFEFF/, '');
  const first = clean.split(/\r?\n/)[0];
  const separator = first.includes(';') ? ';' : first.includes('\t') ? '\t' : ',';
  const rows: string[][] = [];
  let row: string[] = [], cell = '', quoted = false;
  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i];
    if (ch === '"') {
      if (quoted && clean[i + 1] === '"') { cell += '"'; i++; }
      else if (quoted || cell.length === 0) quoted = !quoted;
      else throw new Error('Guillemet invalide dans le CSV.');
    } else if (!quoted && (ch === separator || ch === '\n' || ch === '\r')) {
      row.push(cell); cell = '';
      if (ch !== separator) {
        if (row.some((value) => value.trim())) rows.push(row);
        row = [];
        if (ch === '\r' && clean[i + 1] === '\n') i++;
      }
    } else cell += ch;
  }
  if (quoted) throw new Error('Guillemet non fermé dans le CSV.');
  row.push(cell);
  if (row.some((value) => value.trim())) rows.push(row);
  return rows;
}

export interface PhaseImport {
  buildings: Record<string, string>;
  pipes: Record<number, string>;
  automaticPipes: number[];
  changes: string[];
  errors: string[];
}

export function parsePhases(text: string, plan: Plan, pipeCount: number): PhaseImport {
  const rows = csvRows(text);
  const header = rows.shift()?.map((value) => value.trim());
  if (!header || !['type', 'id', 'annee', 'mode'].every((name) => header.includes(name))) {
    throw new Error('Colonnes requises : type, id, annee, mode. Utilisez le fichier exporté.');
  }
  const result: PhaseImport = { buildings: {}, pipes: {}, automaticPipes: [], changes: [], errors: [] };
  const seen = new Set<string>();
  const automaticRequests: { index: number; year: string; line: number }[] = [];
  const route = routeOf(plan, pipeCount);
  rows.forEach((row, i) => {
    const get = (name: string) => row[header.indexOf(name)]?.trim() ?? '';
    const type = get('type'), id = get('id'), year = get('annee'), mode = get('mode');
    const fail = (message: string) => result.errors.push(`Ligne ${i + 2} : ${message}`);
    if (row.length !== header.length) return fail('nombre de colonnes incorrect.');
    if (!YEARS.includes(year)) return fail(`année attendue entre ${YEARS[0]} et ${YEARS[YEARS.length - 1]}.`);
    if (seen.has(`${type}:${id}`)) return fail(`doublon ${type} ${id}.`);
    seen.add(`${type}:${id}`);
    if (type === 'batiment') {
      if (!plan.buildingIds?.includes(id)) return fail(`bâtiment ${id} absent du plan.`);
      if (mode !== 'manuel') return fail('les bâtiments sont phasés en mode manuel V1.');
      result.buildings[id] = year;
    } else if (type === 'canalisation') {
      const index = Number(id) - 1;
      if (!/^[1-9]\d*$/.test(id) || !route.indexes.includes(index)) return fail(`canalisation ${id} absente du tracé.`);
      if (mode !== 'auto' && mode !== 'manuel') return fail('mode attendu : auto ou manuel.');
      if (mode === 'auto') {
        automaticRequests.push({ index, year, line: i + 2 });
        result.automaticPipes.push(index);
      } else result.pipes[index] = year;
    } else return fail(`type inconnu : ${type}.`);
    if (mode !== 'auto') result.changes.push(`${type} ${id} → ${year} (${mode})`);
  });
  const appliedOverrides = { ...plan.pipePhaseOverrides, ...result.pipes };
  automaticRequests.forEach(({ index }) => { delete appliedOverrides[index]; });
  const resultingPhases = routeOf({
    ...plan,
    buildingPhases: { ...plan.buildingPhases, ...result.buildings },
    pipePhaseOverrides: appliedOverrides,
  }, pipeCount).phases;
  automaticRequests.forEach(({ index, year, line }) => {
    if (year !== route.phases[index] && year !== resultingPhases[index]) {
      result.errors.push(`Ligne ${line} : date automatique modifiée ; indiquez le mode manuel pour imposer une année.`);
    }
    result.changes.push(`canalisation ${index + 1} → ${resultingPhases[index]} (auto)`);
  });
  if (!rows.length) result.errors.push('Le fichier ne contient aucune ligne.');
  return result;
}
